const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.pumperlens.com/api';

export interface TokenMetric {
  _id?: string;
  symbol: string;
  name?: string;
  imageUrl?: string;
  image_url?: string;
  hhi: number;
  gini_coefficient: number;
  holder_distribution: {
    top_holder: number;
    top_10: number;
    top_25: number;
    top_50: number;
    top_100: number;
    top_250: number;
    top_500: number;
    top_1000: number;
  };
  lorenz_curve: {
    holders: number[];
    holdings: number[];
  };
  total_holders: number;
  last_updated: string;
  mint: string;
  market_cap?: number;
  volume_24h?: number;
  price_change_24h?: number | null;
  top_holders: Array<{
    address: string;
    owner: string;
    amount: number;
    delegated_amount: number;
    total_amount: number;
    label?: string;
    is_exchange?: boolean;
  }>;
  color?: string;
  price_history?: Array<{
    timestamp: string;
    price: number;
  }>;
  holder_counts_by_usd?: {
    total: number;
    above_10: number;
    above_100: number;
    above_500: number;
    above_1000: number;
    above_10000: number;
    above_100000: number;
    above_1000000: number;
    above_10_percent: number;
    above_100_percent: number;
    above_500_percent: number;
    above_1000_percent: number;
    above_10000_percent: number;
    above_100000_percent: number;
    above_1000000_percent: number;
  };
  market_cap_per_holder?: {
    above_10: number;
    above_100: number;
    above_1000: number;
    above_10000: number;
    above_100000: number;
    above_1000000: number;
    total: number;
  };
  total_supply?: number;
  exchange_supply?: number;
  exchange_supply_percentage?: number;
  current_price?: number;
  decimals?: number;
  co_ownership?: Array<{
    mint: string;
    percentage: number;
    name?: string;
    symbol?: string;
    image_url?: string;
  }>;
}

export const fetchTokenMetrics = async (): Promise<TokenMetric[]> => {
  try {
    console.log('Fetching token metrics from:', `${API_BASE_URL}/token-metrics/`);
    const response = await fetch(`${API_BASE_URL}/token-metrics/`);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch token metrics:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Failed to fetch token metrics: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    console.log('Received token metrics data:', data);
    return data.tokens;
  } catch (error) {
    console.error('Error fetching token metrics:', error);
    return [];
  }
};

export const fetchTokenDetail = async (mintAddress: string): Promise<TokenMetric> => {
  try {
    const response = await fetch(`${API_BASE_URL}/token-metrics/${mintAddress}/`);
    if (!response.ok) {
      throw new Error('Failed to fetch token detail');
    }
    const data = await response.json();
    return data.token;
  } catch (error) {
    console.error('Error fetching token detail:', error);
    throw error;
  }
};

export async function getTokenMetrics(mint: string, excludeExchanges: boolean = true): Promise<TokenMetric> {
  const response = await fetch(`${API_BASE_URL}/token/${mint}/metrics?exclude_exchanges=${excludeExchanges}`);
  if (!response.ok) {
    throw new Error('Failed to fetch token metrics');
  }
  const data = await response.json();
  // Ensure price_history is always an array
  return {
    ...data,
    price_history: data.price_history || []
  };
} 